import { useCallback, useEffect, useState } from 'react';
import { replace } from 'store/router/actions';
import qs from 'qs';
import { useDispatch } from 'react-redux';
import { useIsAppInstanceConfigNexudus } from 'hooks/use-is-app-instance-config-nexudus.hook';
import { useLocation } from 'react-router';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { DEFAULT_TYPE_COMMA_DELIMITER, DEFAULT_TYPE_DELIMITER } from 'shared/consts';
import { useGetCurrentLocationResourcesTypesQuery } from 'store/resources/hooks';
import { useGetResourceCollectionsQuery } from 'store/collections/hooks';
import { ResourceCollection } from 'store/collections/types';

export interface FilterChip {
  value: string;
  label: string;
  type: 'type' | 'collection';
}

interface UseFilterBarReturnValues {
  isCurrentConfigNexudus: boolean;
  isFilteredOptionsAreVisible: boolean;
  filterByTypeEncoded: string;
  filterByCollectionEncoded: string;
  dateRangeEncoded: string;
  startTimeEncoded: string;
  endTimeEncoded: string;
  onCancelClick: (value: string) => void;
  chips: FilterChip[];
  isFavoritesOnly: boolean;
  onFavoritesOnlyClick: VoidFunction;
}

type ExtendedResourceCollection = ResourceCollection & { originalUuids?: string[] };

export const getSelectedTypes = (
  filterByTypesString: string,
  resourcesTypes: Array<string> | undefined,
): Array<string> => {
  if (!resourcesTypes) return [];
  const selectedTypes: Array<string> = [];
  let currentType = '';
  for (let i = 0; i < filterByTypesString.length; i += 1) {
    if (filterByTypesString[i] !== ',') {
      currentType += filterByTypesString[i];
      if (i === filterByTypesString.length - 1 && resourcesTypes.includes(currentType)) {
        selectedTypes.push(currentType);
        currentType = '';
      }
    }
    if (filterByTypesString[i] === ',') {
      if (!resourcesTypes.includes(currentType)) {
        currentType += filterByTypesString[i];
      } else {
        selectedTypes.push(currentType);
        currentType = '';
      }
    }
  }

  return selectedTypes;
};

export const getSelectedCollections = (
  filterByCollectionString: string,
  collections: ExtendedResourceCollection[] | undefined,
): ExtendedResourceCollection[] => {
  if (!filterByCollectionString || !collections?.length) {
    return [];
  }

  const collectionUuids = filterByCollectionString
    .replace(new RegExp(DEFAULT_TYPE_COMMA_DELIMITER, 'g'), ',')
    .split(',')
    .filter(Boolean);

  return collections.filter(collection =>
    collectionUuids.some(
      uuid => collection.uuid === uuid || (collection.originalUuids && collection.originalUuids.includes(uuid)),
    ),
  );
};

export const useFilterBar = (): UseFilterBarReturnValues => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [chips, setChips] = useState<FilterChip[]>([]);
  const {
    filterByType: filterByTypeEncoded,
    filterByCollection: filterByCollectionEncoded,
    startDate: dateRangeEncoded,
    startTime: startTimeEncoded,
    endTime: endTimeEncoded,
    capacity,
    duration,
    filterByFloor,
    favoritesOnly,
  } = useSearchParams();

  const isFavoritesOnly = favoritesOnly === 'true';

  const onFavoritesOnlyClick = useCallback(() => {
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

    const updatedQueryParams = { ...queryParams };
    if (isFavoritesOnly) {
      delete updatedQueryParams.favoritesOnly;
    } else {
      updatedQueryParams.favoritesOnly = 'true';
    }

    const queryString = qs.stringify(updatedQueryParams);
    dispatch(replace(`${location.pathname}?${queryString}`));
  }, [dispatch, location, isFavoritesOnly]);

  const isCurrentConfigNexudus = useIsAppInstanceConfigNexudus();

  const isFilteredOptionsAreVisible =
    Boolean(filterByTypeEncoded) ||
    Boolean(filterByCollectionEncoded) ||
    Boolean(capacity) ||
    Boolean(duration) ||
    Boolean(filterByFloor);

  const onCancelClick = useCallback(
    (value: string) => {
      const chipToRemove = chips.find(chip => chip.value === value);
      if (!chipToRemove) return;

      const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

      if (chipToRemove.type === 'type') {
        const remainingTypes = chips
          .filter(chip => chip.type === 'type' && chip.value !== chipToRemove.value)
          .map(chip => chip.value.replace(',', DEFAULT_TYPE_COMMA_DELIMITER));

        const updatedQueryParams =
          remainingTypes.length > 0 ? { ...queryParams, filterByType: remainingTypes.join(',') } : { ...queryParams };

        if (remainingTypes.length === 0) {
          delete updatedQueryParams.filterByType;
        }

        const queryString = qs.stringify(updatedQueryParams);
        dispatch(replace(`${location.pathname}?${queryString}`));
      } else if (chipToRemove.type === 'collection') {
        const remainingCollections = chips
          .filter(chip => chip.type === 'collection' && chip.value !== chipToRemove.value)
          .map(chip => chip.value.replace(',', DEFAULT_TYPE_COMMA_DELIMITER));

        const updatedQueryParams =
          remainingCollections.length > 0
            ? { ...queryParams, filterByCollection: remainingCollections.join(',') }
            : { ...queryParams };

        if (remainingCollections.length === 0) {
          delete updatedQueryParams.filterByCollection;
        }

        const queryString = qs.stringify(updatedQueryParams);
        dispatch(replace(`${location.pathname}?${queryString}`));
      }

      setChips(chips.filter(chip => chip.value !== chipToRemove.value || chip.type !== chipToRemove.type));
    },
    [dispatch, location, chips],
  );

  const { data: resourceTypes } = useGetCurrentLocationResourcesTypesQuery();
  const { data: collections } = useGetResourceCollectionsQuery();

  const getCollectionLabel = useCallback((collection: ExtendedResourceCollection): string => {
    if (collection.translations?.en?.name) {
      return collection.translations.en.name;
    }
    return collection.name || 'Unnamed Collection';
  }, []);

  useEffect(() => {
    const allChips: FilterChip[] = [];

    if (typeof filterByTypeEncoded === 'string' && filterByTypeEncoded.length > 0) {
      const selectedTypes = getSelectedTypes(filterByTypeEncoded.replace(DEFAULT_TYPE_DELIMITER, ''), resourceTypes);
      const typeChips: FilterChip[] = selectedTypes.map(type => ({
        value: type,
        label: type,
        type: 'type',
      }));
      allChips.push(...typeChips);
    }

    if (typeof filterByCollectionEncoded === 'string' && filterByCollectionEncoded.length > 0) {
      const selectedCollections = getSelectedCollections(
        filterByCollectionEncoded.replace(DEFAULT_TYPE_DELIMITER, ''),
        collections as ExtendedResourceCollection[],
      );
      const collectionChips: FilterChip[] = selectedCollections.map(collection => ({
        value: collection.uuid,
        label: getCollectionLabel(collection),
        type: 'collection',
      }));
      allChips.push(...collectionChips);
    }

    setChips(allChips);
  }, [filterByTypeEncoded, filterByCollectionEncoded, resourceTypes, collections, getCollectionLabel]);

  return {
    isFilteredOptionsAreVisible,
    isCurrentConfigNexudus,
    filterByTypeEncoded: filterByTypeEncoded as string,
    filterByCollectionEncoded: filterByCollectionEncoded as string,
    dateRangeEncoded: dateRangeEncoded as string,
    startTimeEncoded: startTimeEncoded as string,
    endTimeEncoded: endTimeEncoded as string,
    onCancelClick,
    chips,
    isFavoritesOnly,
    onFavoritesOnlyClick,
  };
};
